import {
    ETables,
    IDBMessage,
    IMessage,
    IAPIMessage,
    TDBID,
    TMarkName,
    TNodeName,
    IAPIMessageBroadcast,
    TTable,
    TFollowUpType,
} from '@ab-task/types';
import { normalize, ID2GUID, GUID2ID, GUID2HASH, HASH2GUID } from './core';
import { CoreError } from '@ab-task/errors';
import { Node, Schema } from 'prosemirror-model';

export function messageDB2JS(dbMessage: IDBMessage): IMessage {
    return {
        id: dbMessage.m_id,
        content: normalize(dbMessage.m_content),
        version: dbMessage.m_version,
        isFirst: dbMessage.m_first,
        isLast: dbMessage.m_first,
        creatorId: dbMessage.m_created_by,
        authorIds: dbMessage.m_author_ids.split(',').map(idStr => Number(idStr)),
        createdAt: dbMessage.m_created_at,
        updatedAt: dbMessage.m_updated_at,
        workspaceId: normalize(dbMessage.m_workspace_id),
        groupId: normalize(dbMessage.m_group_id),
        talkId: normalize(dbMessage.m_talk_id),
        documentId: normalize(dbMessage.m_document_id),
        projectId: normalize(dbMessage.m_project_id),
        milestoneId: normalize(dbMessage.m_milestone_id),
        epicId: normalize(dbMessage.m_epic_id),
        taskId: normalize(dbMessage.m_task_id),
        watches: dbMessage.m_watches
            ? dbMessage.m_watches.map(([watchedAt, userId]) => ({
                  watchedAt: new Date(watchedAt),
                  userId,
              }))
            : [],
        reactions: dbMessage.m_reactions
            ? dbMessage.m_reactions.map(([reactedAt, emojiId, userId]) => ({
                  userId,
                  emojiId,
                  reactedAt: new Date(reactedAt),
              }))
            : [],
        followUps: dbMessage.m_follow_ups
            ? dbMessage.m_follow_ups.map(([itemType, itemId, anchor, head]) => ({
                  itemType,
                  itemId,
                  anchor: normalize(anchor),
                  head: normalize(head),
              }))
            : [],
    };
}

export function messageDB2API(dbMessage: IDBMessage): IAPIMessage {
    return {
        __typename: 'Message',
        id: ID2GUID(ETables.messages, dbMessage.m_id),
        creatorId: ID2GUID(ETables.users, dbMessage.m_created_by),
        workspaceId: dbMessage.m_workspace_id
            ? ID2GUID(ETables.workspaces, dbMessage.m_workspace_id)
            : undefined,
        groupId: dbMessage.m_group_id ? ID2GUID(ETables.groups, dbMessage.m_group_id) : undefined,
        talkId: dbMessage.m_talk_id ? ID2GUID(ETables.talks, dbMessage.m_talk_id) : undefined,
        documentId: dbMessage.m_document_id
            ? ID2GUID(ETables.documents, dbMessage.m_document_id)
            : undefined,
        projectId: dbMessage.m_project_id
            ? ID2GUID(ETables.projects, dbMessage.m_project_id)
            : undefined,
        milestoneId: dbMessage.m_milestone_id
            ? ID2GUID(ETables.milestones, dbMessage.m_milestone_id)
            : undefined,
        epicId: dbMessage.m_epic_id ? ID2GUID(ETables.epics, dbMessage.m_epic_id) : undefined,
        taskId: dbMessage.m_task_id ? ID2GUID(ETables.tasks, dbMessage.m_task_id) : undefined,
        authorIds: dbMessage.m_author_ids.split(',').map(id => ID2GUID(ETables.users, Number(id))),
        content: dbMessage.m_content,
        version: dbMessage.m_version,
        isFirst: dbMessage.m_first,
        isLast: dbMessage.m_last,
        createdAt: dbMessage.m_created_at.toISOString(),
        updatedAt: dbMessage.m_updated_at.toISOString(),
        watches: dbMessage.m_watches
            ? dbMessage.m_watches.map(([watchedAt, userId]) => ({
                  __typename: 'MessageWatch',
                  userId: ID2GUID('users', userId),
                  watchedAt: new Date(watchedAt).toISOString(),
              }))
            : [],
        reactions: dbMessage.m_reactions
            ? dbMessage.m_reactions.map(([reactedAt, emojiId, userId]) => ({
                  __typename: 'MessageReaction',
                  userId: ID2GUID(ETables.users, userId),
                  emojiId: HASH2GUID(ETables.emojis, emojiId),
                  reactedAt: new Date(reactedAt).toISOString(),
              }))
            : [],
        followUps: dbMessage.m_follow_ups
            ? dbMessage.m_follow_ups.map(([itemType, itemId, anchor, head]) => ({
                  __typename: 'FollowUp',
                  itemGuid: ID2GUID(itemType.toLowerCase() as TTable, itemId),
                  anchor: anchor,
                  head: head,
              }))
            : [],
    };
}

export function messageAPI2Broadcast(apiMessage: IAPIMessage): IAPIMessageBroadcast {
    const { __typename, ...rest } = apiMessage;

    return {
        ...rest,
        watches: apiMessage.watches
            ? apiMessage.watches.map(apiWatch => {
                  const { __typename, ...rest } = apiWatch;
                  return rest;
              })
            : apiMessage.watches,
        reactions: apiMessage.reactions
            ? apiMessage.reactions.map(apiReaction => {
                  const { __typename, ...rest } = apiReaction;
                  return rest;
              })
            : apiMessage.reactions,
        followUps: apiMessage.followUps
            ? apiMessage.followUps.map(apiFollowUp => {
                  const { __typename, ...rest } = apiFollowUp;
                  return rest;
              })
            : apiMessage.followUps,
    };
}

export function messageAPI2JS(apiMessage: IAPIMessage): IMessage {
    return {
        id: GUID2ID(apiMessage.id)[1],
        content: normalize(apiMessage.content),
        version: apiMessage.version,
        isFirst: apiMessage.isFirst,
        isLast: apiMessage.isLast,
        creatorId: GUID2ID(apiMessage.creatorId)[1],
        workspaceId: apiMessage.workspaceId ? GUID2ID(apiMessage.workspaceId)[1] : undefined,
        groupId: apiMessage.groupId ? GUID2ID(apiMessage.groupId)[1] : undefined,
        talkId: apiMessage.talkId ? GUID2ID(apiMessage.talkId)[1] : undefined,
        documentId: apiMessage.documentId ? GUID2ID(apiMessage.documentId)[1] : undefined,
        projectId: apiMessage.projectId ? GUID2ID(apiMessage.projectId)[1] : undefined,
        milestoneId: apiMessage.milestoneId ? GUID2ID(apiMessage.milestoneId)[1] : undefined,
        epicId: apiMessage.epicId ? GUID2ID(apiMessage.epicId)[1] : undefined,
        taskId: apiMessage.taskId ? GUID2ID(apiMessage.taskId)[1] : undefined,
        authorIds: apiMessage.authorIds.map(guid => GUID2ID(guid)[1]),
        createdAt: new Date(apiMessage.createdAt),
        updatedAt: new Date(apiMessage.updatedAt),

        isBig: normalize(apiMessage.isBig),
        isReadonly: normalize(apiMessage.isReadonly),
        attachmentS3Keys: normalize(apiMessage.attachmentS3Keys),
        watches: apiMessage.watches.map(apiWatch => ({
            userId: GUID2ID(apiWatch.userId)[1],
            watchedAt: new Date(apiWatch.watchedAt),
        })),
        reactions: apiMessage.reactions.map(apiReaction => ({
            userId: GUID2ID(apiReaction.userId)[1],
            emojiId: GUID2HASH(apiReaction.emojiId)[1],
            reactedAt: new Date(apiReaction.reactedAt),
        })),
        followUps: apiMessage.followUps.map(apiFollowUp => ({
            itemType: GUID2ID(apiFollowUp.itemGuid)[0].toUpperCase() as TFollowUpType,
            itemId: GUID2ID(apiFollowUp.itemGuid)[1],
            anchor: normalize(apiFollowUp.anchor),
            head: normalize(apiFollowUp.head),
        })),
    };
}

export const message2ChannelGuid = (message: IMessage) => {
    const { workspaceId, epicId, groupId, projectId, taskId, talkId, documentId } = message;

    if (workspaceId) return ID2GUID(ETables.workspaces, workspaceId);
    if (epicId) return ID2GUID(ETables.epics, epicId);
    if (groupId) return ID2GUID(ETables.groups, groupId);
    if (projectId) return ID2GUID(ETables.projects, projectId);
    if (taskId) return ID2GUID(ETables.tasks, taskId);
    if (talkId) return ID2GUID(ETables.talks, talkId);
    if (documentId) return ID2GUID(ETables.documents, documentId);

    throw CoreError.TYPE_ADAPTOR_FAILED({ info: "ChannelId wasn't found in message" });
};

export const messageAPI2ChannelGuid = (apiMessage: IAPIMessage) => {
    const { workspaceId, epicId, groupId, projectId, taskId, talkId, documentId } = apiMessage;

    if (workspaceId) return workspaceId;
    if (epicId) return epicId;
    if (groupId) return groupId;
    if (projectId) return projectId;
    if (taskId) return taskId;
    if (talkId) return talkId;
    if (documentId) return documentId;

    throw CoreError.TYPE_ADAPTOR_FAILED({ info: "ChannelId wasn't found in message" });
};

export default function message2ChannelId(message: IMessage): TDBID {
    if (message.workspaceId) return [ETables.workspaces, message.workspaceId];
    if (message.groupId) return [ETables.groups, message.groupId];
    if (message.talkId) return [ETables.talks, message.talkId];
    if (message.documentId) return [ETables.talks, message.documentId];
    if (message.projectId) return [ETables.projects, message.projectId];
    if (message.milestoneId) return [ETables.milestones, message.milestoneId];
    if (message.epicId) return [ETables.epics, message.epicId];
    if (message.taskId) return [ETables.tasks, message.taskId];

    throw CoreError.TYPE_ADAPTOR_FAILED({ info: "ChannelId wasn't found in message" });
}

export function messageContent2Doc(schema: Schema<TNodeName, TMarkName>, content?: string) {
    let doc: Node | undefined;
    if (typeof content === 'string') {
        try {
            const json = JSON.parse(content);
            doc = Node.fromJSON(schema, json);
        } catch (error: any) {
            console.error(error);
            doc = schema.node('doc', undefined, [
                schema.node('paragraph', undefined, [
                    schema.text('Failed to parse message content'),
                ]),
            ]);
        }
    }

    return doc;
}
